import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { godOffboardingApi } from '../../api';
import { GS, G } from './_godTheme';

function DeleteModal({ company, onClose, onDeleted }) {
  const [typedName, setTypedName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [error,     setError]     = useState('');
  const [step,      setStep]      = useState(1);

  const nameMatches = typedName === company.name;

  const handleDelete = async () => {
    if (!nameMatches) { setError('Company name does not match'); return; }
    setDeleting(true); setError('');
    try {
      await godOffboardingApi.deleteCompany(company.id, typedName);
      setStep(3);
      setTimeout(() => { onDeleted(); onClose(); }, 2000);
    } catch (e) {
      setError(e.response?.data?.message || 'Deletion failed');
      setDeleting(false);
    }
  };

  return (
    <div style={GS.overlay}>
      <div style={{ ...GS.modal, maxWidth: '480px' }}>
        {step === 1 && (<>
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>⚠️</div>
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#DC2626', fontFamily: G.fontBrand }}>Permanent Data Deletion</h3>
          </div>
          <div style={{ padding: '14px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', marginBottom: '16px' }}>
            <p style={{ fontWeight: 700, color: '#DC2626', marginBottom: '6px' }}>This action CANNOT be undone.</p>
            <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7 }}>
              All data for <strong>{company.name}</strong> will be permanently deleted — employees, tasks, audit logs, notifications, everything.
              This action will be recorded in the God Audit Log but the data cannot be recovered.
            </p>
          </div>
          <p style={{ fontSize: '13px', color: G.textMuted, marginBottom: '16px' }}>
            Ensure you have downloaded the company's data export before continuing.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={GS.btnSecondary}>Cancel</button>
            <button onClick={() => setStep(2)} style={{ ...GS.btnDanger, background: '#EF4444', color: '#fff' }}>I understand — Continue</button>
          </div>
        </>)}

        {step === 2 && (<>
          <div style={GS.modalHdr}>
            <h3 style={{ ...GS.modalTitle, color: '#DC2626' }}>Confirm deletion</h3>
            <button onClick={onClose} style={GS.modalClose}>×</button>
          </div>
          <p style={{ fontSize: '14px', color: G.textSub, marginBottom: '12px' }}>
            Type <strong style={{ color: G.text }}>{company.name}</strong> exactly to confirm:
          </p>
          {error && <div style={{ ...GS.errBox, marginBottom: '10px' }}>{error}</div>}
          <input
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
            placeholder={company.name}
            autoFocus
            style={{ ...GS.input, marginBottom: '14px', borderColor: typedName && !nameMatches ? '#EF4444' : '#E5E7EB' }}
          />
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px', cursor: 'pointer' }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: G.textSub, lineHeight: 1.5 }}>
              I confirm I have a data export and understand this action permanently destroys all company data.
            </span>
          </label>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={GS.btnSecondary}>Cancel</button>
            <button onClick={handleDelete} disabled={!nameMatches || !confirmed || deleting}
              style={{ ...GS.btnDanger, background: '#EF4444', color: '#fff', opacity: (!nameMatches || !confirmed) ? 0.5 : 1, cursor: (!nameMatches || !confirmed) ? 'not-allowed' : 'pointer' }}>
              {deleting ? 'Deleting…' : 'Delete All Data Permanently'}
            </button>
          </div>
        </>)}

        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>✓</p>
            <p style={{ fontWeight: 700, color: '#16A34A', fontSize: '16px' }}>Data deleted successfully.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GodOffboarding() {
  const navigate                      = useNavigate();
  const [review,    setReview]        = useState([]);
  const [loading,   setLoading]       = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting]     = useState(null);
  const [error,     setError]         = useState('');
  const [success,   setSuccess]       = useState('');

  const load = useCallback(() => {
    godOffboardingApi.retentionReview()
      .then(r => setReview(r.data.data || []))
      .catch(() => setError('Failed to load retention review list'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (company) => {
    setExporting(company.id); setError('');
    try {
      const res = await godOffboardingApi.exportCompany(company.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `tracern-export-${company.slug}.zip`; a.click();
      URL.revokeObjectURL(url);
      setSuccess(`Export downloaded for ${company.name}`);
    } catch (e) {
      setError('Export failed: ' + (e.response?.data?.message || e.message));
    } finally { setExporting(null); }
  };

  const handleChurn = async (company) => {
    if (!window.confirm(`Mark ${company.name} as CHURNED? This starts the 90-day retention clock.`)) return;
    try {
      await godOffboardingApi.churnCompany(company.id);
      setSuccess(`${company.name} marked as CHURNED`);
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <div style={{ maxWidth: '960px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={GS.title}>Offboarding & Data Retention</h1>
        <p style={GS.sub}>Companies churned for 90+ days appear here for deletion review.</p>
      </div>

      {error   && <div style={GS.errBox}>{error}</div>}
      {success && <div style={GS.sucBox}>{success}</div>}

      {/* Policy notice */}
      <div style={{ padding: '14px 18px', background: G.goldLight, border: `1px solid ${G.goldBorder}`, borderRadius: '12px', marginBottom: '20px' }}>
        <p style={{ fontWeight: 700, color: G.goldDeep, marginBottom: '4px', fontSize: '13px' }}>Data Retention Policy</p>
        <p style={{ fontSize: '12px', color: G.gold, lineHeight: 1.7 }}>
          Companies with CHURNED status for 90+ days appear here for deletion review.
          Always export the company's data before deleting.
          Deletion permanently removes all tenant data and cannot be reversed.
          Every deletion is permanently recorded in the God Audit Log.
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[1,2].map(i => <div key={i} className="skeleton" style={{ height: '80px', borderRadius: '14px' }} />)}
        </div>
      ) : !review.length ? (
        <div style={{ textAlign: 'center', padding: '60px', color: G.textMuted }}>
          <p style={{ fontSize: '32px', marginBottom: '12px' }}>✓</p>
          <p style={{ fontWeight: 600, color: G.textSub, marginBottom: '4px' }}>No companies pending deletion review</p>
          <p style={{ fontSize: '12px' }}>Companies appear here when churned for 90+ days.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {review.map(company => (
            <div key={company.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
              padding: '16px 20px', background: '#fff', borderRadius: '14px',
              border: '1px solid #FECACA', flexWrap: 'wrap',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <p style={{ fontWeight: 700, fontSize: '15px', color: G.text }}>{company.name}</p>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: '#F3F4F6', color: '#6B7280', fontWeight: 700 }}>CHURNED</span>
                  <span style={{ fontSize: '12px', color: '#DC2626', fontWeight: 600 }}>{company.churnedDaysAgo}d since churn</span>
                </div>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: G.textMuted }}>👥 {company.employeeCount} employees</span>
                  <span style={{ fontSize: '12px', color: G.textMuted }}>📋 {company.taskCount} tasks</span>
                  <span style={{ fontSize: '12px', color: G.textFaint }}>{company.slug}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap' }}>
                <button onClick={() => navigate(`/god/companies/${company.id}`)}
                  style={{ padding: '7px 14px', borderRadius: '8px', background: '#F9FAFB', color: G.textSub, border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
                  View
                </button>
                <button onClick={() => handleExport(company)} disabled={exporting === company.id}
                  style={{ padding: '7px 14px', borderRadius: '8px', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                  {exporting === company.id ? 'Exporting…' : '↓ Export'}
                </button>
                <button onClick={() => setDeleteTarget(company)}
                  style={{ padding: '7px 14px', borderRadius: '8px', background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                  Delete All Data
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <DeleteModal company={deleteTarget} onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); setSuccess('Company data permanently deleted'); load(); }} />
      )}
    </div>
  );
}
